import { useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';
import {
  clampEmailBuilderVerticalActionRailLeft,
  EMAIL_BUILDER_VERTICAL_ACTION_RAIL_TOP_OFFSET_PX,
  emailBuilderVerticalActionRailAnchorElement,
  emailBuilderVerticalActionRailAnchorLeft,
} from './emailBuilderVerticalActionRailLayout';
import {
  EMAIL_BUILDER_VERTICAL_ACTION_RAIL_PIN_X_VAR,
  EMAIL_BUILDER_VERTICAL_ACTION_RAIL_PIN_Y_VAR,
  EMAIL_BUILDER_VERTICAL_ACTION_RAIL_VISIBLE_PIN_STYLE,
} from './emailBuilderVerticalActionRailChrome';

const MAIN_CANVAS_ID = 'main-canvas';

function resolveEmailBuilderCanvasScrollRoot(canvasNode: HTMLElement): HTMLElement {
  if (canvasNode.classList.contains('email-builder-canvas--mobile-layout')) {
    return canvasNode;
  }

  const mainCanvas = canvasNode.querySelector(`#${MAIN_CANVAS_ID}`);
  return mainCanvas instanceof HTMLElement ? mainCanvas : canvasNode;
}

function applyRailPinPosition(rail: HTMLElement, left: number, top: number): void {
  rail.style.setProperty(EMAIL_BUILDER_VERTICAL_ACTION_RAIL_PIN_X_VAR, `${left}px`);
  rail.style.setProperty(EMAIL_BUILDER_VERTICAL_ACTION_RAIL_PIN_Y_VAR, `${top}px`);
}

export function useEmailBuilderVerticalActionRailPin(args: {
  enabled: boolean;
  frameRef?: RefObject<HTMLElement | null>;
  railRef: RefObject<HTMLElement | null>;
  useRowGroupAnchor?: boolean;
}): CSSProperties | null {
  const { enabled, frameRef, railRef, useRowGroupAnchor = false } = args;
  const [pinStyle, setPinStyle] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!enabled || frameRef === undefined) {
      setPinStyle(null);
      return;
    }

    let scrollActiveUntil = 0;
    let pinStyleCommitted = false;
    let scrollRafId = 0;

    const runLayout = () => {
      const frame = frameRef.current;
      const rail = railRef.current;
      const canvasNode = frame?.closest('.email-builder-canvas');
      const mainCanvas =
        canvasNode instanceof HTMLElement
          ? canvasNode.querySelector('#main-canvas')
          : null;
      if (
        !frame ||
        !rail ||
        !(canvasNode instanceof HTMLElement) ||
        !(mainCanvas instanceof HTMLElement)
      ) {
        return;
      }

      const scrollRoot = resolveEmailBuilderCanvasScrollRoot(canvasNode);
      const scrollTrack = mainCanvas.querySelector('.editor-canvas-scroll__track');
      if (!(scrollTrack instanceof HTMLElement)) {
        return;
      }
      const frameRect = frame.getBoundingClientRect();
      const canvasRect = mainCanvas.getBoundingClientRect();
      const trackRect = scrollTrack.getBoundingClientRect();
      const clipRect = scrollRoot.getBoundingClientRect();
      const frameFullyOutsideClip =
        frameRect.bottom <= clipRect.top || frameRect.top >= clipRect.bottom;
      const suppressHideDuringScroll = performance.now() < scrollActiveUntil;

      if (frameFullyOutsideClip && !suppressHideDuringScroll) {
        rail.style.visibility = 'hidden';
        rail.style.pointerEvents = 'none';
        return;
      }

      const railWidth = rail.offsetWidth;
      if (railWidth === 0) {
        return;
      }

      if (!pinStyleCommitted) {
        pinStyleCommitted = true;
        setPinStyle(EMAIL_BUILDER_VERTICAL_ACTION_RAIL_VISIBLE_PIN_STYLE);
      }

      rail.style.visibility = 'visible';
      rail.style.pointerEvents = 'auto';

      const anchorLeft = emailBuilderVerticalActionRailAnchorLeft(
        frame,
        frameRect,
        useRowGroupAnchor,
      );
      const leftViewport = clampEmailBuilderVerticalActionRailLeft(
        anchorLeft,
        mainCanvas,
        canvasRect,
        railWidth,
      );
      const left = leftViewport - trackRect.left;
      const top =
        frameRect.top - trackRect.top + EMAIL_BUILDER_VERTICAL_ACTION_RAIL_TOP_OFFSET_PX;
      applyRailPinPosition(rail, left, top);
    };

    const scheduleScrollLayout = () => {
      if (scrollRafId !== 0) {
        return;
      }
      scrollRafId = requestAnimationFrame(() => {
        scrollRafId = 0;
        runLayout();
      });
    };

    let rafId = 0;
    const scheduleLayout = () => {
      if (rafId !== 0) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        runLayout();
      });
    };

    runLayout();

    const frame = frameRef.current;
    const rail = railRef.current;
    const canvasNode =
      frame?.closest('.email-builder-canvas') ??
      rail?.closest('.email-builder-canvas');
    const mainCanvas =
      canvasNode instanceof HTMLElement
        ? canvasNode.querySelector('#main-canvas')
        : null;

    const observer = new ResizeObserver(scheduleLayout);
    if (frame) {
      observer.observe(frame);
    }
    if (rail) {
      observer.observe(rail);
    }
    if (canvasNode instanceof HTMLElement) {
      observer.observe(canvasNode);
      const scaledFrame = canvasNode.querySelector('.email-builder-canvas-frame');
      if (scaledFrame instanceof HTMLElement) {
        observer.observe(scaledFrame);
      }
    }
    if (mainCanvas instanceof HTMLElement) {
      observer.observe(mainCanvas);
      const scrollTrack = mainCanvas.querySelector('.editor-canvas-scroll__track');
      if (scrollTrack instanceof HTMLElement) {
        observer.observe(scrollTrack);
      }
    }
    const scrollRoot =
      canvasNode instanceof HTMLElement
        ? resolveEmailBuilderCanvasScrollRoot(canvasNode)
        : null;
    const onScroll = () => {
      scrollActiveUntil = performance.now() + 200;
      scheduleScrollLayout();
    };

    if (scrollRoot) {
      scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    }

    const anchor = frame ? emailBuilderVerticalActionRailAnchorElement(frame) : null;
    if (anchor) {
      observer.observe(anchor);
    }

    window.addEventListener('resize', scheduleLayout, { passive: true });

    return () => {
      observer.disconnect();
      if (rafId !== 0) {
        cancelAnimationFrame(rafId);
      }
      if (scrollRafId !== 0) {
        cancelAnimationFrame(scrollRafId);
      }
      if (scrollRoot) {
        scrollRoot.removeEventListener('scroll', onScroll);
      }
      if (rail) {
        rail.style.removeProperty(EMAIL_BUILDER_VERTICAL_ACTION_RAIL_PIN_X_VAR);
        rail.style.removeProperty(EMAIL_BUILDER_VERTICAL_ACTION_RAIL_PIN_Y_VAR);
        rail.style.removeProperty('visibility');
        rail.style.removeProperty('pointer-events');
      }
      window.removeEventListener('resize', scheduleLayout);
    };
  }, [enabled, frameRef, railRef, useRowGroupAnchor]);

  return enabled ? pinStyle : null;
}
